import React, { useContext } from 'react';
import { IColumnDef } from '../../../../../../Types/ColumnDefType';
import { IUIUrval } from '../../../../../../Types/UrvalTypes';
import {
  findValue,
  getFormattedColumnValue,
} from '../ObjectTable/ObjectTableRow';
import { EDListItemButton } from '../../../../../../BaseComponents/List/Components/EDListItem';
import FunctionMenu from '../../FunctionMenu/FunctionMenu/FunctionMenu';
import { IClientBusinessModel } from '../../../../../../Types/ClientModelTypes';
import { ModelContext } from '../../../../../../Model/ModelContext/ModelContext';
import {
  IBusinessListIconDef,
  IBusinessColorConfig,
} from '../../../Const/BusinessConfigs';
import { makeStyles } from 'tss-react/mui';
import { getCaption } from '../../../../../../Model/BusinessModelUtils';
import { safeGetAttributeInfo } from '../../../../../../Model/avenyModelUtils';
import {
  getMasked,
  getMaskedFlagFromCurrentValues,
  MASKED_VALUE_TEXT,
} from '../../../Hooks/useAttributeValues';
import BusinessDataListView, {
  IDataListValue,
} from '../BusinessDataListView/BusinessDataListView';
import { memo, useMemo, useCallback } from 'react';
import { useItemFunctionMenuItemsType } from '../../../../../../Utils/ObjektVyUtils';
import FunctionItem from '../../FunctionMenu/FunctionItem/FunctionItem';
import { useDefaultFunctionMenu } from '../../FunctionMenu/DefaultFunctionMenu/useDefaultFunctionMenu';
import { useThrowIfPropHasChanged } from '../../../../../../Hooks/useThrowIfPropHasChanged';
import BusinessDetailContext from '../../BusinessDetailContext/BusinessDetailContext';
import EDIconButton from '../../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import { ICollectionDataType } from '../Queries/BusinessCollectionQuery';
import { TSelectedDetail } from '../../../../../../Types/MasterDetailTypes';
import { useBusinessConfig } from '../../../../../../Components/BusinessConfigContext/BusinessConfigContext';

interface IProps {
  columns: readonly IColumnDef[];
  item: ICollectionDataType;
  nextId: string | null;
  prevId: string | null;
  urval: IUIUrval | null;
  selectDetail?: (id: TSelectedDetail) => void;
  selected: boolean;
  toggleSelected: (id: string) => void;
  // Temp until implementation is decided upon
  showCheckbox?: boolean;
  inCard?: boolean;
  useFunktionsMenyItems?: useItemFunctionMenuItemsType;
  collection: IClientBusinessModel;
  associationName?: string;
  colors: IBusinessColorConfig[];
  numberOfColumns: number;
  businessIcons: IBusinessListIconDef[];
  disabled?: boolean;
  disableDivider?: boolean;
  doNotUseFunktionsMeny?: boolean;
  isNew?: boolean;
  onDeleteItem?: (item: ICollectionDataType) => void;
  miniList?: boolean;
  hideDefaultFunktionsMenyItems?: boolean;
}

const useStyles = makeStyles<
  IProps,
  'item' | 'disabled' | 'hideFunctionMenu' | 'caption'
>()((theme, props, classes) => {
  const deleteIcon = props.onDeleteItem == null ? 0 : 1;
  const functionMenuIcon = 1;
  const customIcons = props.businessIcons ? props.businessIcons.length : 0;
  return {
    item: {
      '&:hover': {
        background: theme.customProperties.Fade.BlueGray.hover,
      },
      [`&:hover .${classes.caption} :first-of-type`]: {
        borderBottom: `2px solid ${theme.palette.primary.light}`,
        marginBottom: -2,
      },
    },
    unselectableItem: {
      cursor: 'default',
      '&:hover': {
        background: 'none',
      },
      [`&:hover .${classes.caption} :first-of-type`]: {
        borderBottom: `2px solid ${theme.palette.primary.light}`,
        marginBottom: -2,
      },
    },
    selectedItem: {
      background: theme.customProperties.Fade.BlueGray.selected,
    },
    listItemGutters: {
      paddingRight: (customIcons + deleteIcon + functionMenuIcon) * 36 - 8,
      paddingLeft: 0,
    },
    functionMenu: {
      opacity: 0,
      transition: 'opacity 100ms ease',
      [`.${classes.item}:hover &`]: {
        opacity: 1,
      },
      [`&.${classes.item}:hover .${classes.hideFunctionMenu}`]: {
        opacity: 0,
      },
    },
    listRoot: {
      [`&.${classes.disabled}`]: {
        opacity: 1,
        color: theme.palette.action.disabled,
      },
    },
    disabled: {},
    test: {
      backgroundColor: theme.customProperties.Legend.darkGreen.full,
    },
    hideFunctionMenu: {},
    isNew: {
      cursor: 'default',
      pointerEvents: 'none',
    },
    iconButton: {
      cursor: 'pointer',
      pointerEvents: 'all',
    },
    caption: {}, // Add this class to styles
  } as const;
});

/**
 *  Komponent som ritar ut key-value pairs för alla kolumner i den ordning de ligger i columns-listan
 */
const BusinessDataListItem: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const { selectDetail, toggleSelected: toggleSelectedProp } = props;
  const { classes, cx } = useStyles(props, {
    props: props as any,
  });
  const { avenyCollectionMap } = useContext(ModelContext);
  const businessconfig = useBusinessConfig(props.collection.CollectionName);
  const handleClick = useCallback(() => {
    if (selectDetail) {
      selectDetail({
        id: props.item.id,
        nextId: props.nextId,
        prevId: props.prevId,
        data: props.item,
      });
    }
  }, [selectDetail, props.item, props.nextId, props.prevId]);

  useThrowIfPropHasChanged(
    'Hook useFunktionsMenyItems',
    props.useFunktionsMenyItems
  );

  const defaultFuntionMenyItems = useDefaultFunctionMenu({
    id: props.item.id,
    collection: props.collection,
    associationName: props.associationName,
    disabled: props.isNew,
  });
  const funktionsMenyItems = props.useFunktionsMenyItems
    ? [
        ...(props.hideDefaultFunktionsMenyItems ? [] : defaultFuntionMenyItems),
        ...props.useFunktionsMenyItems({
          id: props.item.id,
          collection: props.collection,
          associationName: props.associationName,
        }),
      ]
    : props.hideDefaultFunktionsMenyItems
    ? []
    : defaultFuntionMenyItems;

  const visibleFunctionMenyItems = funktionsMenyItems.filter(
    item => !item.hidden
  );

  const colors = useMemo(
    () => props.colors.filter(post => post.condition(props.item)),
    [props.colors, props.item]
  );

  const gridItems: IDataListValue[] = useMemo(
    () =>
      props.columns
        .filter(col => !col.header)
        .flatMap(column => {
          const label = getCaption(
            column,
            props.collection,
            avenyCollectionMap
          );

          const value =
            findValue(props.item, column.attributeDef.split('.')) ?? '';

          const attributeInfo = safeGetAttributeInfo(
            props.collection,
            column.attributeDef,
            avenyCollectionMap
          );

          const maskedFlag = getMaskedFlagFromCurrentValues(
            props.item,
            column.attributeDef
          );

          const optionalLabel =
            businessconfig?.attributes?.[column.attributeDef]?.optionalLabel;
          return attributeInfo.isOk()
            ? [
                {
                  name: label,
                  value: value.toString(),
                  attribute: attributeInfo.value.attribute,
                  hideLabel: column.hideCaption || optionalLabel,
                  masked: getMasked(attributeInfo.value.attribute, maskedFlag),
                },
              ]
            : [];
        }),
    [
      avenyCollectionMap,
      props.collection,
      props.columns,
      props.item,
      businessconfig?.attributes,
    ]
  );
  const toggleSelected = useCallback(
    () => toggleSelectedProp(props.item.id),
    [toggleSelectedProp, props.item.id]
  );

  return (
    <BusinessDetailContext.Provider value={{ data: props.item }}>
      <EDListItemButton
        onClick={props.selectDetail && handleClick}
        selected={props.selected}
        classes={{
          root: classes.listRoot,
          gutters: classes.listItemGutters,
          disabled: props.disabled ? classes.disabled : undefined,
        }}
        className={cx({
          [classes.item]: Boolean(props.selectDetail),
          [classes.unselectableItem]: !props.selectDetail,
          [classes.selectedItem]: props.selected,
          [classes.isNew]: props.isNew,
        })}
        // disableGutters
        disabled={props.disabled}
        disableVerticalPadding
        disableRipple
        divider={!props.disableDivider}
      >
        <BusinessDataListView
          miniList={props.miniList}
          collection={props.collection}
          label={{
            name: props.columns
              .filter(col => col.header)
              .map(x =>
                getCaption({ ...x }, props.collection, avenyCollectionMap)
              )
              .join(' - '),
            value: props.columns
              .filter(col => col.header)
              .map(col => {
                const attrInfo = safeGetAttributeInfo(
                  props.collection,
                  col.attributeDef,
                  avenyCollectionMap
                );
                const maskedFlag = getMaskedFlagFromCurrentValues(
                  props.item,
                  col.attributeDef
                );
                const masked =
                  attrInfo.isOk() &&
                  getMasked(attrInfo.value.attribute, maskedFlag);
                if (masked) {
                  return MASKED_VALUE_TEXT;
                }
                return getFormattedColumnValue(
                  avenyCollectionMap,
                  props.item,
                  col,
                  props.collection
                );
              })
              .join(' - '),
          }}
          values={gridItems}
          numberOfColumns={props.numberOfColumns}
          listItemRightPadding={
            (props.businessIcons ? props.businessIcons.length : 0) + 1
          }
          secondaryAction={
            <>
              {props.businessIcons && (
                <>
                  {props.businessIcons.map(icon =>
                    icon.componentFn({
                      id: props.item.id,
                      data: props.item,
                      disabled: props.disabled,
                    })
                  )}
                </>
              )}
              {props.onDeleteItem && (
                <EDIconButton
                  icon="delete"
                  iconSize="medium"
                  margin="none"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    props.onDeleteItem?.(props.item);
                  }}
                  className={classes.iconButton}
                  padding="dense"
                  tooltip={`Ta bort ${props.collection.RootType.SOLabel}`}
                />
              )}
              {!props.doNotUseFunktionsMeny && (
                <FunctionMenu
                  id={props.item.id}
                  collection={props.collection}
                  disabled={props.disabled || props.isNew}
                >
                  {visibleFunctionMenyItems.map(funktionsMenyItem => (
                    <FunctionItem
                      key={funktionsMenyItem.id}
                      onClick={funktionsMenyItem.onClick}
                      disabled={funktionsMenyItem.disabled}
                      disabledReason={funktionsMenyItem.disabledReason}
                      iconType={funktionsMenyItem.iconType}
                      listItemIcon
                      confirmButtonCaption={
                        funktionsMenyItem.confirmButtonCaption
                      }
                      confirmDialogCaption={
                        funktionsMenyItem.confirmDialogCaption
                      }
                      confirmDialogMessage={
                        funktionsMenyItem.confirmDialogMessage
                      }
                    >
                      {funktionsMenyItem.caption}
                    </FunctionItem>
                  ))}
                </FunctionMenu>
              )}
            </>
          }
          inCard={props.inCard}
          showCheckbox={props.showCheckbox}
          selected={props.selected}
          colors={colors}
          toggleSelected={toggleSelected}
          disabled={props.disabled}
          isNew={props.isNew}
        />
      </EDListItemButton>
    </BusinessDetailContext.Provider>
  );
};

export default memo(BusinessDataListItem);
